import { Plane, ArrowRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { buildWhatsAppLink } from "../lib/i18n";

export const Steps = ({ t, lang }) => {
  const headerRef = useReveal();
  const timelineRef = useReveal({ threshold: 0.2 });

  return (
    <section
      id="steps"
      data-testid="steps-section"
      className="relative py-28 sm:py-40 bg-[#070707] border-t border-white/5 overflow-hidden"
    >
      {/* Decorative large airplane silhouette */}
      <div
        aria-hidden
        className="absolute -top-10 right-0 lg:right-10 opacity-[0.04] pointer-events-none select-none"
      >
        <Plane size={520} strokeWidth={0.5} className="rotate-[-18deg]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div ref={headerRef} className="reveal grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-white/50 mb-6">
              — {t.steps.eyebrow} —
            </p>
            <h2
              data-testid="steps-title"
              className="heading-display text-4xl sm:text-5xl md:text-6xl whitespace-pre-line"
            >
              {t.steps.title}
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-white/55 text-base sm:text-lg max-w-md">{t.steps.sub}</p>
          </div>
        </div>

        {/* Timeline — desktop (horizontal) */}
        <div
          ref={timelineRef}
          data-testid="steps-timeline"
          className="reveal hidden md:block relative pt-10 pb-4"
        >
          {/* Track container */}
          <div className="relative h-1 mx-auto max-w-5xl">
            {/* Base line */}
            <div className="absolute inset-0 bg-white/10 rounded-full" />
            {/* Animated progress */}
            <div className="absolute inset-y-0 left-0 bg-white/60 rounded-full progress-line" style={{ width: "0%" }} />

            {/* Dots */}
            {t.steps.items.map((_step, idx) => {
              const leftPct = idx === 0 ? 0 : idx === 1 ? 50 : 100;
              return (
                <div
                  key={idx}
                  data-testid={`timeline-dot-${idx}`}
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${leftPct}%` }}
                >
                  <div className="relative h-4 w-4 rounded-full bg-white timeline-dot-active" />
                </div>
              );
            })}

            {/* Animated plane traveling along track */}
            <div className="plane-marker" data-testid="timeline-plane">
              <div className="h-10 w-10 rounded-full bg-white text-black flex items-center justify-center shadow-[0_8px_30px_rgba(255,255,255,0.35)]">
                <Plane size={18} strokeWidth={1.6} className="rotate-90" />
              </div>
            </div>
          </div>

          {/* Step labels */}
          <div className="grid grid-cols-3 gap-8 mt-16 max-w-5xl mx-auto">
            {t.steps.items.map((step, idx) => (
              <div
                key={step.n}
                data-testid={`step-label-${idx}`}
                className={`flex flex-col ${
                  idx === 0 ? "items-start text-left" : idx === 1 ? "items-center text-center" : "items-end text-right"
                }`}
              >
                <span className="step-number block mb-4" style={{ fontSize: "clamp(3rem, 7vw, 5rem)" }}>
                  {step.n}
                </span>
                <h3 className="heading-display text-2xl sm:text-3xl mb-3">{step.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed max-w-xs">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline — mobile (vertical) */}
        <div className="md:hidden relative">
          {/* Vertical line */}
          <div className="absolute left-[22px] top-2 bottom-2 w-px bg-white/15" />

          <div className="space-y-12">
            {t.steps.items.map((step, idx) => (
              <div
                key={step.n}
                data-testid={`step-mobile-${idx}`}
                className="relative pl-16"
              >
                {/* Dot with plane on first */}
                <div className="absolute left-0 top-1 flex items-center justify-center">
                  {idx === 0 ? (
                    <div className="h-11 w-11 rounded-full bg-white text-black flex items-center justify-center shadow-[0_6px_24px_rgba(255,255,255,0.3)]">
                      <Plane size={18} strokeWidth={1.6} className="rotate-90" />
                    </div>
                  ) : (
                    <div className="h-4 w-4 rounded-full bg-white ml-[14px] timeline-dot-active" />
                  )}
                </div>
                <span className="step-number block mb-2" style={{ fontSize: "4rem" }}>
                  {step.n}
                </span>
                <h3 className="heading-display text-2xl mb-2">{step.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 flex justify-center">
          <a
            data-testid="steps-cta"
            href={buildWhatsAppLink(
              lang === "fr"
                ? "Bonjour PRESTIGE KEY, je souhaite démarrer une demande personnalisée."
                : "Hello PRESTIGE KEY, I would like to start a bespoke request."
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-luxe"
          >
            {t.steps.cta}
            <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
};
